import { select, Selection } from 'd3-selection'

import TrackChart from '../common/TrackChart'
import { css } from 'emotion'

import { openModal } from './MessagePreview'
import ZoomManager, { ZoomIn, Zoom } from 'live/services/zoom-manager'
import { Options } from '../../types'

const messageCss = css`
    z-index: 11;
    position: absolute;
    //background: #f0f;
    white-space: nowrap;
    font-size: 11px;
    cursor: pointer;

    div.total {
        height: 18px;
        width: 18px;
        text-align: center;
        background: dodgerblue;
        color: #fff;
        display: inline-block;
    }

    div.empty {
        display: none;
    }

    div.text {
        height: 18px;
        line-height: 18px;
        display: inline-block;
        background: #c1c1c1;
        color: #333;
        padding: 0 2px;
    }
`

type State = {
    start: {}
}

type LiveEvent = {
    timestamp: number
    value?: number
}

export default class MessageChart extends TrackChart<State> {
    protected _svg: Selection<HTMLDivElement, unknown, null, undefined>
    private _modal: Selection<HTMLDivElement, unknown, HTMLElement, any>
    private config: DefaultChartConfigs
    private options: Options & { cfg: { internalAxis: boolean } }

    init() {
        super.init()
        this.el.classed('messages-chart', true)
        this._modal = select('body').append('div')
    }

    get internalAxis(): boolean {
        return this.options.cfg?.internalAxis ?? false
    }

    render(data, config): void {
        this.options.cfg = config.widget.jsonConfig
        this.config = config

        super.render(data)

        if (!data) {
            this._contents.selectAll('div').remove()

            return
        }

        if (!data.items.length) {
            this._contents.selectAll('div').remove()
        }

        if (!this.isVertical) {
            this._contents.html('Horizontal not implemented')

            return
        }

        this.updateLayout()

        const scale = this.scale

        if (Array.isArray(data.items) && data.items.length >= 0) {
            const messages = data.items.reduce((state, message) => {
                if (state.length === 0) {
                    state.push({ timestamp: message.timestamp, messages: [message] })
                } else {
                    const latestMessage = state[state.length - 1]
                    const x1 = scale(latestMessage.timestamp)
                    const x2 = scale(message.timestamp)
                    if (Math.abs(x1 - x2) < 22) {
                        latestMessage.messages.push(message)
                    } else {
                        state.push({ timestamp: message.timestamp, messages: [message] })
                    }
                }

                return state
            }, [])

            this._contents
                .selectAll('.' + messageCss)
                .data(messages, (d: LiveEvent) => d.timestamp)
                .join('div')
                .classed(messageCss, true)
                .style('top', (d: LiveEvent) => scale(d.timestamp) + 'px')
                .style('left', d => this.inner.left + 1 + 'px')
                .on('click', (event, d) => {
                    openModal(d, this.spanPickerType)
                })
                .call(function (s) {
                    s.selectAll('div.total')
                        .data(d => [d])
                        .join('div')
                        .classed('total', true)
                        .classed('empty', (d: { messages: LiveEvent[] }) => d.messages.length == 1)
                        .text((d: { messages: LiveEvent[] }) => d.messages.length)
                    s.selectAll('div.text')
                        .data(d => [d])
                        .join('div')
                        .classed('text', true)
                        .text((d: { messages: LiveEvent[] }) => d.messages[0].value)
                })
        }
    }

    destroy() {
        super.destroy()

        if (this._modal) {
            this._modal.remove()
        }
    }

    onMouseMove() {}

    renderLegend() {}

    setAxisExtremes(zoom: ZoomIn | Zoom): void {
        const { min, max } = zoom

        this.chartExtremes = { min, max, spanType: this.getSpanType() }
        ZoomManager.setPreviousZoom({
            widgetType: '', // We don't use the widgetType in new zoom behavior
            spanType: this.getSpanType(),
            min: min,
            max: max,
            newZoomBehavior: true,
            chartInRealTime: false,
            timeZoomOcurred: new Date().getTime()
        })

        const newData = {
            ...this.data,
            customSpan: {
                begin: min,
                end: max
            }
        }

        this.render(newData, this.config)
    }
}
